import { useState, useEffect } from "react";
import MainLayout from "@/components/layout/MainLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { NativeSelect } from "@/components/ui/native-select";
import { API, useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { GripVertical, User, Calendar, AlertTriangle } from "lucide-react";

const columns = [
  { id: "backlog", title: "Backlog", color: "kanban-backlog" },
  { id: "planned", title: "Planejado", color: "kanban-planned" },
  { id: "in-progress", title: "Em Progresso", color: "kanban-in-progress" },
  { id: "waiting-materials", title: "Aguard. Materiais", color: "kanban-waiting-materials" },
  { id: "completed", title: "Concluído", color: "kanban-completed" }
];

const priorityColors = {
  low: "bg-slate-100 text-slate-700",
  medium: "bg-blue-100 text-blue-700",
  high: "bg-amber-100 text-amber-700",
  urgent: "bg-red-100 text-red-700"
};

const priorityLabels = {
  low: "Baixa",
  medium: "Média",
  high: "Alta",
  urgent: "Urgente"
};

export default function KanbanPage() {
  const { user } = useAuth();
  const [cards, setCards] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draggingCard, setDraggingCard] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [cardsRes, usersRes] = await Promise.all([
        axios.get(`${API}/kanban`),
        axios.get(`${API}/users`)
      ]);
      setCards(cardsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (e, card) => {
    setDraggingCard(card);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = async (e, columnId) => {
    e.preventDefault();
    if (!draggingCard || draggingCard.status === columnId) return;

    try {
      await axios.put(`${API}/kanban/${draggingCard.id}/status?status=${columnId}`);
      setCards(cards.map(card =>
        card.id === draggingCard.id ? { ...card, status: columnId } : card
      ));
      toast.success("Status atualizado!");
    } catch (error) {
      toast.error("Erro ao atualizar status");
    }
    setDraggingCard(null);
  };

  const handleAssign = async (cardId, userId) => {
    try {
      await axios.put(`${API}/kanban/${cardId}/assign?assignee_id=${userId}`);
      await fetchData();
      toast.success("Card atribuído!");
    } catch (error) {
      toast.error("Erro ao atribuir card");
    }
  };

  const getCardsByColumn = (columnId) => {
    return cards.filter(card => card.status === columnId);
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-luvcorp-navy"></div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="kanban-page">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-heading font-bold text-luvcorp-navy">Quadro Kanban</h1>
          <Badge variant="outline" className="text-sm">
            {cards.length} cards
          </Badge>
        </div>

        <div className="overflow-x-auto pb-4">
          <div className="flex gap-4 min-w-max">
            {columns.map((column) => (
              <div
                key={column.id}
                className="w-72 flex-shrink-0"
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, column.id)}
              >
                <Card className={`border-0 shadow-sm ${column.color}`}>
                  <CardHeader className="py-3 px-4">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm font-semibold text-luvcorp-navy">
                        {column.title}
                      </CardTitle>
                      <Badge variant="secondary" className="text-xs">
                        {getCardsByColumn(column.id).length}
                      </Badge>
                    </div>
                  </CardHeader>
                </Card>
                
                <ScrollArea className="h-[calc(100vh-280px)] mt-2">
                  <div className="space-y-2 pr-2">
                    {getCardsByColumn(column.id).map((card) => (
                      <Card
                        key={card.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, card)}
                        className={`border-0 shadow-sm cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow ${
                          draggingCard?.id === card.id ? "opacity-50" : ""
                        }`}
                        data-testid={`kanban-card-${card.id}`}
                      >
                        <CardContent className="p-3">
                          <div className="flex items-start gap-2 mb-2">
                            <GripVertical className="h-4 w-4 text-slate-300 flex-shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0">
                              <Badge className={`${priorityColors[card.priority]} text-xs mb-2`}>
                                {priorityLabels[card.priority] || card.priority}
                              </Badge>
                              <h4 className="font-medium text-sm text-luvcorp-navy truncate">
                                {card.title}
                              </h4>
                              <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                                {card.description}
                              </p>
                            </div>
                          </div>
                          
                          <div className="flex items-center justify-between mt-3 pt-2 border-t">
                            <div className="flex items-center gap-1 text-xs text-slate-500">
                              <Calendar className="h-3 w-3" />
                              {new Date(card.created_at).toLocaleDateString('pt-BR')}
                            </div>
                            
                            {user?.role !== "operational" && (
                              <NativeSelect
                                value={card.assignee_id || ""}
                                onValueChange={(value) => handleAssign(card.id, value)}
                                placeholder="Atribuir"
                                options={users.filter(u => u.role !== "client").map(u => ({ value: u.id, label: u.name }))}
                                className="w-24 h-7 text-xs"
                              />
                            )}
                          </div>
                          
                          {card.assignee && (
                            <div className="flex items-center gap-1 mt-2 text-xs text-slate-600">
                              <User className="h-3 w-3" />
                              {card.assignee}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                    
                    {getCardsByColumn(column.id).length === 0 && (
                      <div className="p-4 text-center text-sm text-slate-400 border-2 border-dashed rounded-lg">
                        Arraste cards aqui
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </div>
            ))}
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
